import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskLines, FadeUp, Word } from "../components/Motion";
import { TaglineMarquee } from "../components/TaglineMarquee";
import { IMAGES, COLLECTIONS, MANIFESTO } from "../data/content";

const ease = [0.16, 1, 0.3, 1];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#121212]">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Arkaari editorial" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#121212]/55 via-[#121212]/20 to-[#121212]/75" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-[1500px] mx-auto px-5 sm:px-8 flex flex-col justify-end pb-16 sm:pb-20">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="overline text-[#D9BE86] mb-6"
        >
          A Customizable Fashion House · Est. by Ankita Mangtani
        </motion.p>

        <MaskLines
          className="text-[#F5F2EA]"
          lineClass="font-serif font-light leading-[0.92] text-[15vw] sm:text-[12vw] lg:text-[10rem] tracking-tight"
          lines={["Arkaari"]}
          delay={0.35}
        />

        <div className="mt-4 sm:mt-6 max-w-xl">
          <MaskLines
            lineClass="font-serif italic font-light text-2xl sm:text-3xl text-[#F5F2EA]/90"
            lines={["Handcrafted stories,", "worn with grace."]}
            delay={0.7}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9, ease }}
          className="flex flex-wrap items-center gap-4 mt-10"
        >
          <Link to="/custom-orders" data-testid="hero-custom-cta" className="btn-gold px-8 py-4 overline">
            <span className="inline-flex items-center gap-2">Begin a Custom Order <ArrowUpRight size={16} strokeWidth={1.5} /></span>
          </Link>
          <Link to="/collections" data-testid="hero-collections-cta" className="btn-outline px-8 py-4 overline !border-white/50 !text-white">
            <span>View Collections</span>
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden sm:flex items-center gap-2 text-[#F5F2EA]/70"
      >
        <span className="overline">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} strokeWidth={1.5} />
        </motion.span>
      </motion.div>
    </section>
  );
};

const Intro = () => (
  <section data-testid="brand-intro" className="max-w-[1500px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <div className="lg:col-span-5 img-frame rounded-sm">
        <img src={IMAGES.editorialDoor} alt="Arkaari atelier" className="w-full h-[70vh] object-cover rounded-sm" />
      </div>
      <div className="lg:col-span-7">
        <FadeUp>
          <p className="overline text-[#C6A15B] mb-8">The House of Arkaari</p>
        </FadeUp>
        <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
          <Word delay={0}>Where&nbsp;</Word><Word delay={0.05}>heritage&nbsp;</Word><Word delay={0.1}>craft&nbsp;</Word>
          <Word delay={0.15}>meets&nbsp;</Word><Word delay={0.2}>a&nbsp;</Word><Word delay={0.25} className="italic text-[#C6A15B]">modern&nbsp;</Word>
          <Word delay={0.3} className="italic text-[#C6A15B]">hand.</Word>
        </h2>
        <FadeUp delay={0.2}>
          <p className="text-[#7a7568] text-lg leading-relaxed mt-8 max-w-2xl">
            Arkaari is a customizable clothing house for women and men, built on the belief that
            what you wear should be made for you — not the other way around. Every garment is drawn,
            draped and stitched in our atelier, blending timeless Indian craftsmanship with a clean,
            contemporary aesthetic.
          </p>
        </FadeUp>
        <div className="grid grid-cols-3 gap-6 mt-12 border-t border-[#D5D0C5] pt-10">
          {[["100%", "Handcrafted"], ["Made", "To measure"], ["2026", "Since"]].map(([a, b], i) => (
            <FadeUp key={i} delay={0.1 * i}>
              <p className="font-serif text-4xl sm:text-5xl font-light">{a}</p>
              <p className="overline text-[#7a7568] mt-2">{b}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Featured = () => (
  <section data-testid="featured-collections" className="bg-[#121212] text-[#F5F2EA] py-24 sm:py-32 grain relative">
    <div className="max-w-[1500px] mx-auto px-5 sm:px-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-16">
        <div>
          <p className="overline text-[#D9BE86] mb-5">Featured Collections</p>
          <h2 className="font-serif font-light text-4xl sm:text-6xl tracking-tight leading-none">
            Four worlds, <span className="italic">one hand.</span>
          </h2>
        </div>
        <Link to="/collections" className="overline text-[#D9BE86] link-underline inline-flex items-center gap-2 self-start sm:self-auto">
          All collections <ArrowUpRight size={16} strokeWidth={1.5} />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {COLLECTIONS.map((c, i) => (
          <FadeUp
            key={c.id}
            delay={i * 0.08}
            className={`${i % 4 === 0 || i % 4 === 3 ? "md:col-span-7" : "md:col-span-5"}`}
          >
            <Link to="/collections" data-testid={`featured-${c.id}`} className="group block img-frame rounded-sm relative">
              <img src={c.image} alt={c.title} className="w-full h-[52vh] object-cover rounded-sm" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 p-7">
                <span className="font-serif text-[#D9BE86]/80 text-lg">{c.index}</span>
                <h3 className="font-serif text-3xl sm:text-4xl font-light mt-1">{c.title}</h3>
                <p className="text-white/70 text-sm mt-1">{c.tagline}</p>
              </div>
              <span className="absolute top-6 right-6 overline text-white/70">{c.pieces}</span>
            </Link>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const ManifestoTeaser = () => (
  <section data-testid="manifesto-teaser" className="max-w-[1500px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
    <FadeUp>
      <p className="overline text-[#C6A15B] mb-12">Our Manifesto</p>
    </FadeUp>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#D5D0C5] border border-[#D5D0C5]">
      {MANIFESTO.map((m, i) => (
        <FadeUp key={m.n} delay={i * 0.1} className="bg-[#F5F2EA] p-8 sm:p-10">
          <p className="font-serif text-6xl font-light text-[#C6A15B]/60">{m.n}</p>
          <h3 className="font-serif text-2xl sm:text-3xl font-light mt-6">{m.title}</h3>
          <p className="text-[#7a7568] leading-relaxed mt-4">{m.body}</p>
        </FadeUp>
      ))}
    </div>
  </section>
);

const CustomBand = () => (
  <section data-testid="custom-band" className="relative overflow-hidden">
    <div className="img-frame">
      <img src={IMAGES.atelier1} alt="Custom atelier" className="w-full h-[70vh] object-cover" />
    </div>
    <div className="absolute inset-0 bg-[#121212]/60" />
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 text-[#F5F2EA]">
      <FadeUp>
        <p className="overline text-[#D9BE86] mb-6">Your vision, our craft</p>
        <h2 className="font-serif font-light text-4xl sm:text-6xl lg:text-7xl tracking-tight max-w-4xl leading-[1.02]">
          Dreaming of something <span className="italic">made only for you?</span>
        </h2>
        <Link to="/custom-orders" data-testid="band-custom-cta" className="btn-gold inline-flex mt-10 px-10 py-4 overline">
          <span className="inline-flex items-center gap-2">Start your custom order <ArrowUpRight size={16} strokeWidth={1.5} /></span>
        </Link>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <TaglineMarquee />
      <Intro />
      <Featured />
      <ManifestoTeaser />
      <CustomBand />
    </>
  );
}
